import React, { useEffect, useState } from 'react'
import styles from './InvoiceDeliveryDrawer.module.scss'
import { Drawer, message, Tooltip } from 'antd'
import { Button, InputNumber } from '@surecompanies/core_components'
import DownOutlined from '@ant-design/icons/DownOutlined'
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined'
import LeftOutlined from '@ant-design/icons/LeftOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import { isNil } from 'lodash'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  InvoiceDeliveryItemDto,
  InvoiceTemplate,
  ParseWarning,
} from '../../api/dto/invoice-delivery.dto'
import { useUpdateInvoiceDeliveryMetadataMutation } from '../../api/invoice-delivery-api-client'
import { StatusBadge, VersionBadge } from './BulkInvoiceUpload.styles'
import {
  DrawerBody,
  DrawerFileCard,
  DrawerFileCardBody,
  DrawerFileCardIcon,
  DrawerFileCardMeta,
  DrawerFileCardName,
  DrawerHeaderTitle,
  DrawerEditCard,
  DrawerInlineEdit,
  DrawerInlineEditCellRef,
  DrawerInlineEditHint,
  DrawerInlineEditLabel,
  DrawerInlineEditRow,
  DrawerNavButton,
  DrawerNavRow,
  DrawerNavPosition,
  DrawerSection,
  DrawerSectionTitle,
} from './InvoiceDeliveryDrawer.styles'
import { DeliveryHistoryPanel } from './DeliveryHistoryPanel'
import { DueDateEditor } from './DueDateEditor'
import { OeSplitEditor } from './OeSplitEditor'
import { UploadedMeta } from './UploadedMeta'
import { DeliveryRatioBadge } from './DeliveryRatioBadge'
import { DrawBySubCompanySection } from './DrawBySubCompanySection'
import { EmailDeliveryResults, SHOWABLE_STATUSES } from './EmailDeliveryResults'
import { EmailRecipientsSection } from './EmailRecipientsSection'
import { MatchCompanySection } from './MatchCompanySection'
import { EmailPreviewSection } from './emailTemplates/EmailPreviewSection'
import { readTotalDue } from './emailTemplates/oeSplit'
import {
  cleanParseWarningRawValue,
  formatAmountInput,
  parseAmountInput,
  resolveStatusBadgeKey,
  resolveStatusLabel,
} from './utils'

interface InvoiceDeliveryDrawerProps {
  delivery: InvoiceDeliveryItemDto | null
  /** Every delivery listed for the month, so OE1's preview can quote its OE2. */
  monthDeliveries: InvoiceDeliveryItemDto[]
  open: boolean
  onClose: () => void
  onSetArchived: (id: string, isArchived: boolean) => void
  onDelete: (id: string) => void
  /** 0-based index of `delivery` within the currently filtered list. */
  currentIndex?: number
  /** Total number of rows in the currently filtered list. */
  totalInList?: number
  canNavigatePrev?: boolean
  canNavigateNext?: boolean
  onNavigate?: (direction: 'prev' | 'next') => void
}

const InvoiceDeliveryDrawer: React.FC<InvoiceDeliveryDrawerProps> = ({
  delivery,
  monthDeliveries,
  open,
  onClose,
  onSetArchived,
  onDelete,
  currentIndex,
  totalInList,
  canNavigatePrev,
  canNavigateNext,
  onNavigate,
}) => {
  const [localDelivery, setLocalDelivery] = useState(delivery)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const { flags } = useFeatureFlags()
  const isStripeIntegrationEnabled =
    flags['admin.finance.stripe-integration-enabled'] === true

  useEffect(() => {
    setLocalDelivery(delivery)
    setIsHistoryOpen(false)
  }, [delivery])

  if (!localDelivery) return null

  const isUnmatched = isNil(localDelivery.clientId)

  const billingPeriodLabel = new Date(
    localDelivery.billingPeriodStart + 'T00:00:00'
  ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  const showNav = !isNil(onNavigate) && (totalInList ?? 0) > 1
  const positionLabel =
    !isNil(currentIndex) && currentIndex >= 0 && !isNil(totalInList)
      ? `${currentIndex + 1} of ${totalInList}`
      : null

  return (
    <Drawer
      title={
        <DrawerHeaderTitle>
          <span>{localDelivery.clientName ?? 'Unmatched Company'}</span>
          <StatusBadge $status={resolveStatusBadgeKey(localDelivery)}>
            {resolveStatusLabel(localDelivery)}
          </StatusBadge>
          <DeliveryRatioBadge stats={localDelivery.emailStats} />
        </DrawerHeaderTitle>
      }
      open={open}
      onClose={onClose}
      width={480}
    >
      <DrawerBody>
        {showNav && (
          <DrawerNavRow>
            <Tooltip title="Previous delivery">
              <DrawerNavButton
                type="button"
                onClick={() => onNavigate?.('prev')}
                disabled={!canNavigatePrev}
                aria-label="Previous delivery"
              >
                <LeftOutlined />
              </DrawerNavButton>
            </Tooltip>
            {positionLabel && (
              <DrawerNavPosition>{positionLabel}</DrawerNavPosition>
            )}
            <Tooltip title="Next delivery">
              <DrawerNavButton
                type="button"
                onClick={() => onNavigate?.('next')}
                disabled={!canNavigateNext}
                aria-label="Next delivery"
              >
                <RightOutlined />
              </DrawerNavButton>
            </Tooltip>
          </DrawerNavRow>
        )}
        {/*
         * File "hero" card — filename + version badge, then billing period,
         * upload time, and who uploaded this version.
         */}
        <DrawerFileCard>
          <DrawerFileCardIcon>
            <FileExcelOutlined />
          </DrawerFileCardIcon>
          <DrawerFileCardBody>
            <DrawerFileCardName>
              <span className={styles.drawerFileName}>
                {localDelivery.fileName}
              </span>
              {localDelivery.version > 1 && (
                <VersionBadge>v{localDelivery.version}</VersionBadge>
              )}
            </DrawerFileCardName>
            <DrawerFileCardMeta>
              <span>{billingPeriodLabel}</span>
              <UploadedMeta
                createdAt={localDelivery.createdAt}
                uploaderName={localDelivery.createdByName}
                uploaderEmail={localDelivery.createdByEmail}
              />
            </DrawerFileCardMeta>
          </DrawerFileCardBody>
        </DrawerFileCard>

        <EmailPreviewSection
          delivery={localDelivery}
          monthDeliveries={monthDeliveries}
          onUpdated={setLocalDelivery}
        />

        {/* Match company — shown when the upload couldn't be auto-linked */}
        {isUnmatched && (
          <DrawerSection>
            <DrawerSectionTitle>Match Company</DrawerSectionTitle>
            <MatchCompanySection
              deliveryId={localDelivery.id}
              open={open}
              onMatched={setLocalDelivery}
            />
          </DrawerSection>
        )}

        {/* Parsed Excel fields + inline editor for unparseable Total Due. */}
        <InvoiceSummarySection
          delivery={localDelivery}
          monthDeliveries={monthDeliveries}
          onUpdated={setLocalDelivery}
        />

        {/* Draw by sub-company — only present on deliveries with a Stripe payment list */}
        {isStripeIntegrationEnabled &&
          localDelivery.paymentDetail?.paymentList &&
          localDelivery.paymentDetail.paymentList.length > 0 && (
            <DrawerSection>
              <DrawerSectionTitle>Stripe Payments</DrawerSectionTitle>
              <DrawBySubCompanySection
                paymentList={localDelivery.paymentDetail.paymentList}
                canRetry={localDelivery.isLatest && !localDelivery.isArchived}
              />
            </DrawerSection>
          )}

        {/* Email recipients — only shown once delivery is matched to a company */}
        {!isUnmatched && (
          <DrawerSection>
            <EmailRecipientsSection
              clientId={localDelivery.clientId ?? ''}
              clientName={localDelivery.clientName}
              open={open}
            />
          </DrawerSection>
        )}

        {SHOWABLE_STATUSES.has(localDelivery.status) && (
          <DrawerSection>
            <DrawerSectionTitle>Email Delivery</DrawerSectionTitle>
            <EmailDeliveryResults
              deliveryId={localDelivery.id}
              status={localDelivery.status}
              collapsible={false}
            />
          </DrawerSection>
        )}

        {/*
         * Version History — collapsed by default. The panel only mounts
         * (and fires its history query) once the operator expands the
         * section, keeping open-time work minimal.
         */}
        {localDelivery.version > 1 && (
          <DrawerSection>
            <DrawerSectionTitle>
              <button
                type="button"
                onClick={() => setIsHistoryOpen((v) => !v)}
                aria-expanded={isHistoryOpen}
                className={styles.sectionToggle}
              >
                {isHistoryOpen ? (
                  <DownOutlined className={styles.chevronIcon} />
                ) : (
                  <RightOutlined className={styles.chevronIcon} />
                )}
                <span>
                  Version History · {localDelivery.version - 1} earlier
                </span>
              </button>
            </DrawerSectionTitle>
            {isHistoryOpen && (
              <DeliveryHistoryPanel
                deliveryId={localDelivery.id}
                onSetArchived={onSetArchived}
                onDelete={onDelete}
              />
            )}
          </DrawerSection>
        )}
      </DrawerBody>
    </Drawer>
  )
}

const InvoiceSummarySection: React.FC<{
  delivery: InvoiceDeliveryItemDto
  monthDeliveries: InvoiceDeliveryItemDto[]
  onUpdated: (delivery: InvoiceDeliveryItemDto) => void
}> = ({ delivery, monthDeliveries, onUpdated }) => {
  // The backend only sends warnings that still need fixing, so any warning
  // here is actionable regardless of what the metadata currently holds.
  const totalDueWarning = delivery.parseWarnings?.[0] ?? null

  const isHistorical = !delivery.isLatest

  if (delivery.invoiceTemplate === InvoiceTemplate.OE_FIRST) {
    return (
      <DrawerSection>
        <DrawerSectionTitle>Invoice Summary</DrawerSectionTitle>
        <OeSplitEditor
          key={`oe-split-${delivery.id}`}
          delivery={delivery}
          monthDeliveries={monthDeliveries}
          part1Warning={totalDueWarning}
          isHistorical={isHistorical}
          onUpdated={onUpdated}
        />
      </DrawerSection>
    )
  }

  return (
    <DrawerSection>
      <DrawerSectionTitle>Invoice Summary</DrawerSectionTitle>
      <DrawerEditCard>
        <TotalDueEditor
          key={delivery.id}
          deliveryId={delivery.id}
          currentValue={readTotalDue(delivery)}
          warning={totalDueWarning}
          isHistorical={isHistorical}
          onUpdated={onUpdated}
        />

        <DueDateEditor
          key={`due-date-${delivery.id}`}
          delivery={delivery}
          isHistorical={isHistorical}
          onUpdated={onUpdated}
        />
      </DrawerEditCard>
    </DrawerSection>
  )
}

interface TotalDueEditorProps {
  deliveryId: string
  currentValue: number | null
  warning: ParseWarning | null
  isHistorical: boolean
  onUpdated: (delivery: InvoiceDeliveryItemDto) => void
}

/*
 * Always shown, like the due date: an amount read cleanly from the sheet can
 * still need correcting, e.g. when an OE1/OE2 split was entered on the wrong
 * invoice. A parse warning keeps its own explanation.
 */
const TotalDueEditor: React.FC<TotalDueEditorProps> = ({
  deliveryId,
  currentValue,
  warning,
  isHistorical,
  onUpdated,
}) => {
  const [draft, setDraft] = useState<number | null>(null)
  const amount = draft ?? currentValue
  // While a warning shows, saving the amount already in the field clears it.
  const canSave =
    (!isNil(draft) && draft !== currentValue) ||
    (!isNil(warning) && !isNil(amount))
  const mutation = useUpdateInvoiceDeliveryMetadataMutation({
    onSuccess: (updated) => {
      message.success('Total Due saved')
      setDraft(null)
      onUpdated(updated)
    },
    onError: (err) => {
      message.error(err.message || 'Failed to save Total Due')
    },
  })

  const handleSave = () => {
    if (isNil(amount) || !Number.isFinite(amount)) {
      message.error('Enter a valid number')
      return
    }
    mutation.mutate({ id: deliveryId, body: { totalDue: amount } })
  }

  const invalidRawValue = cleanParseWarningRawValue(warning?.rawValue)
  const needsFix = !isNil(warning) || isNil(currentValue)

  return (
    <DrawerInlineEdit>
      <DrawerInlineEditLabel htmlFor={`total-due-${deliveryId}`}>
        Total due
      </DrawerInlineEditLabel>
      {needsFix ? (
        <DrawerInlineEditHint $tone="warning">
          <WarningOutlined style={{ marginRight: 6 }} />
          {warning?.kind === 'invalid' ? (
            <>
              We couldn&apos;t read <strong>{warning.label}</strong> from cell{' '}
              <DrawerInlineEditCellRef>
                {warning.cellAddress}
              </DrawerInlineEditCellRef>
              {invalidRawValue ? (
                <>
                  {' '}
                  (it contains <code>&quot;{invalidRawValue}&quot;</code>).
                </>
              ) : (
                '.'
              )}{' '}
              Enter the correct amount below, or fix the Excel file and
              re-upload.
            </>
          ) : warning?.kind === 'missing' && warning.cellAddress ? (
            <>
              <strong>{warning.label}</strong> is missing — cell{' '}
              <DrawerInlineEditCellRef>
                {warning.cellAddress}
              </DrawerInlineEditCellRef>{' '}
              is empty. Enter the amount below, or fill the cell and re-upload.
            </>
          ) : (
            <>
              <strong>{warning?.label ?? 'Total Due'}</strong> is missing from
              this invoice. Enter the amount below, or add it to the Excel file
              and re-upload.
            </>
          )}
        </DrawerInlineEditHint>
      ) : null}
      <DrawerInlineEditRow>
        <InputNumber
          value={amount ?? undefined}
          onChange={(v) => setDraft(typeof v === 'number' ? v : null)}
          placeholder="0.00"
          min={0}
          precision={2}
          step={0.01}
          prefix="$"
          formatter={formatAmountInput}
          parser={parseAmountInput}
          style={{ width: '100%' }}
          id={`total-due-${deliveryId}`}
          disabled={isHistorical || mutation.isPending}
        />
        <Button
          type="primary"
          onClick={handleSave}
          loading={mutation.isPending}
          disabled={isHistorical || !canSave}
        >
          Save
        </Button>
      </DrawerInlineEditRow>
      {isHistorical ? (
        <DrawerInlineEditHint>
          This is a historical version — only the latest version can be edited.
        </DrawerInlineEditHint>
      ) : needsFix ? null : (
        <DrawerInlineEditHint>
          This amount is what the emailed invoice will show.
        </DrawerInlineEditHint>
      )}
    </DrawerInlineEdit>
  )
}

export { InvoiceDeliveryDrawer }
